import PropTypes from "prop-types";
import { Box, Typography, Avatar, Divider } from "@material-ui/core";
import moment from "moment";
import TicketStatusChip from "../ticket-status-chip";
import SlaIndicator from "../sla-indicator";

const PRIORITY_CONFIG = {
  critical: { label: "Critical", bgColor: "#FEF2F2", color: "#DC2626" },
  high: { label: "High", bgColor: "#FFF7ED", color: "#EA580C" },
  medium: { label: "Medium", bgColor: "#EFF6FF", color: "#0369A1" },
  low: { label: "Low", bgColor: "#F0FDF4", color: "#16A34A" },
};

const MODULE_LABELS = {
  stock_management: "Stock Management",
  qr_serial: "QR Code / Verification",
  campaigns: "Campaigns & Loyalty",
  dealers: "Dealer Management",
  reports: "Reports & Analytics",
  account_billing: "Account & Settings",
  other: "Other",
};

const PanelRow = ({ label, children }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      py: 1,
      borderBottom: "1px solid #E2E8F0",
      "&:last-child": { borderBottom: "none" },
    }}
  >
    <Typography sx={{ fontSize: 13, color: "#64748B" }}>{label}</Typography>
    <Box>{children}</Box>
  </Box>
);

const SectionTitle = ({ children }) => (
  <Typography
    sx={{
      fontSize: 12,
      fontWeight: 600,
      textTransform: "uppercase",
      color: "#64748B",
      letterSpacing: 0.5,
      mb: 1.5,
    }}
  >
    {children}
  </Typography>
);

const TicketInfoPanel = ({ ticket }) => {
  if (!ticket) return null;

  const priority = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.medium;
  const reporterInitials = ticket.reporter_name
    ? ticket.reporter_name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "?";

  return (
    <Box sx={{ padding: "24px", borderLeft: { xs: "none", md: "1px solid #E2E8F0" }, borderTop: { xs: "1px solid #E2E8F0", md: "none" }, width: { xs: "100%", md: 340 }, position: { md: "sticky" }, top: { md: 0 } }}>
      <Box sx={{ mb: 3 }}>
        <SectionTitle>Ticket Information</SectionTitle>
        <PanelRow label="Status">
          <TicketStatusChip status={ticket.status} />
        </PanelRow>
        <PanelRow label="Priority">
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "4px 10px",
              borderRadius: "12px",
              backgroundColor: priority.bgColor,
              color: priority.color,
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: priority.color,
              }}
            />
            {priority.label}
          </Box>
        </PanelRow>
        <PanelRow label="Category">
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#0F172A" }}>
            {MODULE_LABELS[ticket.smartkood_module] || ticket.smartkood_module}
          </Typography>
        </PanelRow>
        <PanelRow label="Created">
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#0F172A" }}>
            {moment(ticket.created_at).format("MMM D, YYYY")}
          </Typography>
        </PanelRow>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Box sx={{ mb: 3 }}>
        <SectionTitle>Assigned To</SectionTitle>
        {ticket.assignee_name ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "12px",
              backgroundColor: "#EFF6FF",
              borderRadius: "8px",
            }}
          >
            <Avatar
              sx={{
                width: 40,
                height: 40,
                backgroundColor: "#0369A1",
                color: "#fff",
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              {ticket.assignee_name
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .slice(0, 2)}
            </Avatar>
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#0F172A" }}>
              {ticket.assignee_name}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              padding: "12px",
              backgroundColor: "#F8FAFC",
              borderRadius: "8px",
            }}
          >
            <Typography sx={{ fontSize: 13, fontStyle: "italic", color: "#64748B" }}>
              Unassigned
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Box sx={{ mb: 3 }}>
        <SectionTitle>Requester</SectionTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "12px",
            backgroundColor: "#F8FAFC",
            borderRadius: "8px",
          }}
        >
          <Avatar
            sx={{
              width: 40,
              height: 40,
              backgroundColor: "#E8ECF1",
              color: "#0F172A",
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            {reporterInitials}
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#0F172A" }}>
              {ticket.reporter_name}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#64748B" }}>
              {ticket.reporter_email}
            </Typography>
            {ticket.reporter_company_name && (
              <Typography sx={{ fontSize: 11, color: "#64748B" }}>
                {ticket.reporter_company_name}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Box>
        <SectionTitle>SLA Details</SectionTitle>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <SlaIndicator
            label="First Response"
            dueDate={ticket.sla_response_due}
            respondedAt={ticket.first_response_at}
          />
          <SlaIndicator
            label="Resolution"
            dueDate={ticket.sla_resolution_due}
            respondedAt={ticket.resolved_at}
          />
        </Box>
      </Box>
    </Box>
  );
};

TicketInfoPanel.propTypes = {
  ticket: PropTypes.object,
};

export default TicketInfoPanel;
